import { formatCoachingTime } from '@efdigital/ll-addon-coaching';
import { Heading } from '@efdigital/ll-addon-ui';
import type { CoachingCoacheeCohortSessionsModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/sessions';
import { PAGE_WRAPPER_CHROME } from '@efdigital/ll-xceed-v2/utils/layout';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { pageTitle } from 'ember-page-title';

interface CoachingCoacheeCohortSessionsSignature {
  Args: {
    model: CoachingCoacheeCohortSessionsModel;
    controller: unknown;
  };
}

export default class CoachingCoacheeCohortSessions extends Component<CoachingCoacheeCohortSessionsSignature> {
  @service declare router: RouterService;

  ariaCurrent = (routeName: string): 'page' | undefined =>
    this.router.currentRouteName === routeName ? 'page' : undefined;

  get actionRequiredCountBadgeClass(): string {
    const active =
      this.router.currentRouteName ===
      'coaching.coachee.cohort.sessions.action-required';

    return active
      ? 'badge rounded-pill bg-body text-primary ms-1 fw-semibold'
      : 'badge rounded-pill text-bg-warning ms-1 fw-semibold';
  }

  <template>
    {{pageTitle "Sessions"}}

    <div class={{PAGE_WRAPPER_CHROME}}>
      <Heading @size="lg" @text="Sessions" @class="mb-4" />
      <div
        class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4"
      >
        {{! Route links, not ARIA tabs: each view has its own URL. }}
        <nav class="overflow-x-auto" aria-label="Session views">
          <ul
            class="nav nav-pills ll-bg-raised rounded-pill p-1 shadow-sm d-inline-flex flex-nowrap mb-0"
          >
            <li class="nav-item">
              <LinkTo
                @route="coaching.coachee.cohort.sessions.index"
                @models={{array @model.program_id @model.cohort_id}}
                class="nav-link rounded-pill px-3 px-md-4 fw-semibold"
                aria-current={{this.ariaCurrent
                  "coaching.coachee.cohort.sessions.index"
                }}
              >Upcoming</LinkTo>
            </li>
            <li class="nav-item">
              <LinkTo
                @route="coaching.coachee.cohort.sessions.action-required"
                @models={{array @model.program_id @model.cohort_id}}
                class="nav-link rounded-pill px-3 px-md-4 fw-semibold"
                aria-current={{this.ariaCurrent
                  "coaching.coachee.cohort.sessions.action-required"
                }}
              >Action required
                {{#if @model.actionRequiredCount}}
                  <span class={{this.actionRequiredCountBadgeClass}}>
                    {{@model.actionRequiredCount}}
                    <span class="visually-hidden">
                      sessions need attention</span>
                  </span>
                {{/if}}
              </LinkTo>
            </li>
            <li class="nav-item">
              <LinkTo
                @route="coaching.coachee.cohort.sessions.past"
                @models={{array @model.program_id @model.cohort_id}}
                class="nav-link rounded-pill px-3 px-md-4 fw-semibold"
                aria-current={{this.ariaCurrent
                  "coaching.coachee.cohort.sessions.past"
                }}
              >History</LinkTo>
            </li>
          </ul>
        </nav>

        {{#if @model.planner.coachHasBeenChosen}}
          <div
            class="d-flex flex-wrap align-items-center column-gap-3 row-gap-1"
          >
            <div
              class="ll-bg-raised border rounded-3 shadow-sm d-flex align-items-center gap-3 px-3 py-2"
            >
              <iconify-icon
                inline
                icon={{if
                  @model.planner.quota.coaching.remaining
                  "ph:credit-card"
                  "ph:warning-circle"
                }}
                class="{{if
                    @model.planner.quota.coaching.remaining
                    'text-primary'
                    'text-danger'
                  }}
                  fs-5"
                aria-hidden="true"
              />
              <div class="lh-1">
                <p class="gud-eyebrow mb-0 text-body-secondary">Coaching
                  Duration</p>
                <span
                  class="fw-bold small
                    {{unless
                      @model.planner.quota.coaching.remaining
                      'text-danger'
                    }}"
                >{{#if
                    @model.planner.quota.coaching.remaining
                  }}{{formatCoachingTime
                      @model.planner.quota.coaching.remaining
                    }}
                    remaining{{else}}No coaching time remaining{{/if}}</span>
              </div>
            </div>
            {{#let
              (@model.planner.chosenCoachPartnership)
              as |chosenCoachPartnership|
            }}
              {{#if chosenCoachPartnership}}
                {{#if
                  (@model.planner.canBookCoachingSession
                    chosenCoachPartnership.coachUserId
                  )
                }}
                  <LinkTo
                    @route="coaching.coachee.cohort.sessions.book-session"
                    @models={{array @model.program_id @model.cohort_id}}
                    class="btn btn-primary"
                  >
                    <iconify-icon
                      inline
                      icon="ph:calendar-plus"
                      aria-hidden="true"
                    />
                    Book session
                  </LinkTo>
                {{/if}}
              {{/if}}
            {{/let}}
          </div>
        {{/if}}
      </div>
    </div>

    {{outlet}}
  </template>
}
